import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { employeesAPI } from '../../lib/api';
import { CompetencyRuler, bandOf } from './CompetencyRuler';

const ScoreRow = ({ item, value, onChange, idx }) => {
  const band = bandOf(value.nota);
  return (
    <div className="p-3 rounded-lg border border-slate-200 space-y-2" data-testid={`competency-row-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-sm text-slate-900">{item.competencia}</p>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${band.chip}`} data-testid={`competency-band-${idx}`}>{band.label}</span>
          <span className="text-lg font-bold w-10 text-right" data-testid={`competency-score-${idx}`}>{value.nota}</span>
        </div>
      </div>
      <Slider value={[value.nota]} min={0} max={100} step={1} onValueChange={(v) => onChange({ ...value, nota: v[0] })}
        data-testid={`competency-slider-${idx}`} />
      <CompetencyRuler nota={value.nota} meta={value.meta === '' ? undefined : value.meta} />
      <div className="flex gap-2 items-center">
        <Input placeholder="Observação (opcional)" value={value.observacao} onChange={(e) => onChange({ ...value, observacao: e.target.value })}
          className="h-8 text-sm" data-testid={`competency-obs-${idx}`} />
        <label className="text-xs text-slate-500 shrink-0">Meta</label>
        <Input type="number" min={0} max={100} value={value.meta}
          onChange={(e) => onChange({ ...value, meta: e.target.value === '' ? '' : Math.max(0, Math.min(100, Number(e.target.value))) })}
          onBlur={() => value.meta === '' && onChange({ ...value, meta: 0 })}
          className="h-8 w-20 text-sm" data-testid={`competency-meta-${idx}`} />
      </div>
    </div>
  );
};

export const CompetencyAssessmentDialog = ({ open, onClose, employee, data, onSaved }) => {
  const [values, setValues] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && data) {
      setValues(data.itens.map((i) => ({ nota: i.nota ?? 50, observacao: i.observacao || '', meta: i.meta })));
    }
  }, [open, data]);

  const save = async () => {
    setSaving(true);
    try {
      await employeesAPI.createCompetencyAssessment(employee.id, data.itens.map((it, i) => ({
        competencia: it.competencia, nota: values[i].nota, observacao: values[i].observacao,
      })));
      const metasAlteradas = {};
      data.itens.forEach((it, i) => {
        const meta = values[i].meta === '' ? 0 : values[i].meta;
        if (meta !== it.meta) metasAlteradas[it.competencia] = meta;
      });
      if (Object.keys(metasAlteradas).length && data.cargo_id) {
        await employeesAPI.updateCompetencyTargets(data.cargo_id, metasAlteradas);
      }
      toast.success('Avaliação de competências registrada');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar avaliação');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="competency-assessment-dialog">
        <DialogHeader>
          <DialogTitle>Avaliar competências</DialogTitle>
          <DialogDescription>
            {employee?.nome} — régua de 0 a 100. A meta vale para todos os funcionários do cargo {data?.cargo_titulo}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {data?.itens.map((it, i) => values[i] && (
            <ScoreRow key={it.competencia} idx={i} item={it} value={values[i]}
              onChange={(v) => setValues((prev) => prev.map((p, j) => (j === i ? v : p)))} />
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="competency-cancel-btn">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="competency-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Salvar avaliação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
